import React from 'react';

import {
    UI_REGION,
    UpdateCareerGoalSelected,
    GoalCollectionTargetPage,
} from '@udemy/browse-event-tracking';
import {
    DiscoveryUnit,
    DiscoveryUnitItem,
    PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE,
} from '@udemy/discovery-api';
import {DeviceType} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CourseUnit,
    DiscoveryUnitsContainer,
    PopularTopicsUnit,
    SkillsHubCourseUnit,
} from '@udemy/react-discovery-units';

import {personalizeDiscoveryUnitPropData} from 'src/utils/personalize-course-unit-props-helper';
import {getTabTrackingContext} from 'src/utils/tab-tracking-helpers';

import {LabsDiscoveryUnit} from './labs-discovery-unit.react-component';

interface DiscoveryUnitsSectionProps {
    enableSkillBasedCourseUnitPP: boolean;
    enableNewSkillBasedCourseUnitUIPP: boolean;
    enableLabsInPersonalPlan: boolean;
    isTechLearner: boolean;
    deviceType: DeviceType;
    isMobile: boolean;
    showVerticalScroll: boolean;
    useCatalogCourseCards: boolean;
    className?: string;
}

export const DiscoveryUnitsSection: React.FC<DiscoveryUnitsSectionProps> = ({
    enableSkillBasedCourseUnitPP,
    enableNewSkillBasedCourseUnitUIPP,
    enableLabsInPersonalPlan,
    isTechLearner,
    deviceType,
    isMobile,
    showVerticalScroll,
    useCatalogCourseCards,
    className,
}) => {
    const {gettext} = useI18n();

    const responsiveUnit = {
        fullWidth: isMobile,
        showPager: !isMobile,
        layout: isMobile ? (showVerticalScroll ? 'singlecol' : 'multirow') : 'singlerow',
        disableInfiniteScroll: isMobile && showVerticalScroll,
        maxCards: isMobile && showVerticalScroll ? 3 : undefined,
        showAddToCartInCourseCard: false,
        showInstructorDetails: true,
        courseCardProps:
            isMobile && useCatalogCourseCards ? {showDetails: true, size: 'large'} : undefined,
    };

    const getUnitComponentAndProps = (
        unit: Partial<DiscoveryUnit<DiscoveryUnitItem>> | undefined,
    ) => {
        if (!unit) throw new Error('Missing unit');

        const baseProps = {
            ...responsiveUnit,
            ...unit,
        };

        // Handle different unit types based on view_type and type
        if (
            unit.view_type === 'skill_interest_discovery_unit' &&
            enableNewSkillBasedCourseUnitUIPP
        ) {
            return {
                component: SkillsHubCourseUnit,
                componentProps: {
                    ...baseProps,
                    ...personalizeDiscoveryUnitPropData(
                        gettext('Edit skill interests'),
                        '/personalize/skills',
                        new UpdateCareerGoalSelected(
                            GoalCollectionTargetPage.SKILLS,
                            UI_REGION.COURSE_RECOMMENDATION_UNIT,
                        ),
                        false,
                        false,
                        UI_REGION.COURSE_RECOMMENDATION_UNIT,
                    ),
                    tabTrackingContext: getTabTrackingContext(UI_REGION.COURSE_RECOMMENDATION_UNIT),
                },
            };
        }

        if (unit.type === 'popular_topics' || unit.type === 'bestseller_labels') {
            return {
                component: PopularTopicsUnit,
                componentProps: {
                    ...baseProps,
                },
            };
        }

        if (
            unit.type === 'top' &&
            (unit.view_type === 'horizontal_tabbed' || unit.view_type === 'default')
        ) {
            return {
                component: SkillsHubCourseUnit,
                componentProps: {
                    ...baseProps,
                    ...personalizeDiscoveryUnitPropData(
                        gettext('Edit skill interests'),
                        '/personalize/skills',
                        new UpdateCareerGoalSelected(
                            GoalCollectionTargetPage.SKILLS,
                            UI_REGION.COURSE_RECOMMENDATION_UNIT,
                        ),
                        false,
                        false,
                        UI_REGION.COURSE_RECOMMENDATION_UNIT,
                    ),
                    tabTrackingContext: getTabTrackingContext(UI_REGION.COURSE_RECOMMENDATION_UNIT),
                },
            };
        }

        // Default to CourseUnit for other cases
        return {
            component: CourseUnit,
            componentProps: {
                ...baseProps,
                carouselProps: {
                    ariaLabel: unit.title,
                    ariaRoleDescription: 'carousel',
                },
            },
        };
    };

    const discoveryContainerFilter = (u: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
        // Filter out skills based course recommendations if user not in experiment
        if (u.view_type === 'skill_interest_discovery_unit' && !enableSkillBasedCourseUnitPP) {
            return false;
        }
        // Filter out old skills based course recommendations if user in new experiment
        if (u.view_type === 'skill_interest_discovery_unit' && enableNewSkillBasedCourseUnitUIPP) {
            return false;
        }
        // Filter out new skills based course recommendations if user not in experiment
        // Gate only `top` + `default` on the new UI experiment. `top` + `horizontal_tabbed` is a
        // valid SkillsHub layout from discovery APIs and must render even when the experiment is off
        // (see CVR-1023).
        if (u.type === 'top' && u.view_type === 'default' && !enableNewSkillBasedCourseUnitUIPP) {
            return false;
        }
        // Filter out units not part of this discovery unit container
        return (
            u.view_type !== 'lectures_by_topic' &&
            u.type !== 'algorithm_based_labs' &&
            !['assessments', 'algorithm_based_assessments_with_filter'].includes(u.type || '')
        );
    };

    const shouldShowOccBasedLabsRecoUnit = enableLabsInPersonalPlan && isTechLearner;

    return (
        <div className={className}>
            <DiscoveryUnitsContainer
                pageType={PAGE_TYPE_SUBS_LOGGED_IN_HOMEPAGE}
                deviceType={deviceType}
                getUnitComponentAndProps={getUnitComponentAndProps}
                filter={discoveryContainerFilter}
                fetchOptions={{pageSize: 3}}
            >
                {shouldShowOccBasedLabsRecoUnit && (
                    <LabsDiscoveryUnit
                        enableLabsInPersonalPlan={enableLabsInPersonalPlan}
                        isTechLearner={isTechLearner}
                        deviceType={deviceType}
                    />
                )}
            </DiscoveryUnitsContainer>
        </div>
    );
};
